// filepath: apps/web/src/features/admin/webhooks/EndpointConflictDialog.tsx
//
// FR-UI-ADM-6 AC-4 -- modal dialog that surfaces the
// FR-UI-CORE-6 refetch / merge / discard UX when the server
// returns HTTP 409 ENDPOINT_VERSION_MISMATCH.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import type { VersionConflictError } from "./types";

interface EndpointConflictDialogProps {
  open: boolean;
  conflict: VersionConflictError | null;
  onRefetch: () => void;
  onMerge: () => void;
  onDiscard: () => void;
  onClose: () => void;
}

export function EndpointConflictDialog({
  open,
  conflict,
  onRefetch,
  onMerge,
  onDiscard,
  onClose,
}: EndpointConflictDialogProps): React.ReactElement | null {
  const { t } = useTranslation();
  if (!open || conflict === null) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="endpoint-conflict-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="endpoint-conflict-dialog"
    >
      <div className="w-full max-w-md rounded-md border border-border bg-background p-4 shadow-lg">
        <h2 id="endpoint-conflict-title" className="text-sm font-semibold">
          {t("admin.webhooks.dialog.conflict.title")}
        </h2>
        <p className="mt-2 text-xs text-muted-foreground">{conflict.message}</p>
        <p className="mt-2 text-xs text-muted-foreground">
          Server version: {conflict.currentVersion}
        </p>
        <div className="mt-3 flex justify-end gap-2">
          <button
            type="button"
            className="rounded border border-border px-3 py-1 text-xs"
            onClick={onDiscard}
            data-testid="endpoint-conflict-discard"
          >
            {t("admin.webhooks.dialog.conflict.discard")}
          </button>
          <button
            type="button"
            className="rounded border border-border px-3 py-1 text-xs"
            onClick={onMerge}
            data-testid="endpoint-conflict-merge"
          >
            {t("admin.webhooks.dialog.conflict.refetch")}
          </button>
          <button
            type="button"
            className="rounded border border-border px-3 py-1 text-xs"
            onClick={onRefetch}
            data-testid="endpoint-conflict-refetch"
          >
            {t("admin.webhooks.dialog.conflict.refetch")}
          </button>
          <button
            type="button"
            className="rounded border border-border px-3 py-1 text-xs"
            onClick={onClose}
            data-testid="endpoint-conflict-close"
          >
            {t("common.cancel")}
          </button>
        </div>
      </div>
    </div>
  );
}
